import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { useParams } from 'react-router-dom';
import { Input, Spinner, Text, Button, makeStyles } from '@fluentui/react-components';
import { SearchRegular, ChevronRightRegular, ChevronDownRegular, WarningRegular } from '@fluentui/react-icons';
import { AppTopBar } from '../components/AppTopBar.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import { formatoDeMoneda } from '../lib/money.js';
import {
  getCatalog,
  listCatalogResources,
  listCatalogComponents,
  type Catalog,
  type CatalogResource,
  type CatalogComponent,
} from '../api/catalogs.js';
import { ApiClientError } from '../api/client.js';
import { puedeVerDefectosDelCatalogo } from '@supernova/shared';
import { useIsEcosoftStaff } from '../stores/authStore.js';

const PAGE = 50;

// Tipos de recurso ECOSTOS.
const TIPO: Record<number, string> = {
  1: 'Material', 2: 'Mano de obra', 3: 'Equipo', 4: 'Auxiliar', 6: 'Básico', 7: 'APU',
};
const tipoLabel = (t: number) => TIPO[t] ?? `Tipo ${t}`;
// Los precios de un catálogo van EN SU PROPIA MONEDA (`catalog.currency`), no en
// pesos mexicanos: un catálogo chileno mostraba CLP con signo de peso mexicano.
// Si el catálogo no declara moneda (los que sube el cliente), va sin símbolo.
const qty = (n: number | null) => (n == null ? '' : n.toLocaleString('es-MX', { maximumFractionDigits: 4 }));

const useStyles = makeStyles({
  page: { minHeight: '100vh', background: 'var(--bg-page)' },
  container: { maxWidth: '1080px', margin: '0 auto', padding: '24px 28px' },
  title: { fontFamily: 'var(--font-display)', fontSize: '23px', fontWeight: 700, letterSpacing: '-0.3px', margin: 0, display: 'block' },
  subtitle: { color: 'var(--text-secondary)', fontSize: '12.5px', marginTop: '5px', display: 'block' },
  aviso: { display: 'flex', alignItems: 'center', gap: '8px', margin: '14px 0 0', fontSize: '12.5px', color: 'var(--warn)' },
  toolbar: { display: 'flex', alignItems: 'center', gap: '12px', margin: '18px 0 12px' },
  search: { flex: 1, maxWidth: '420px' },
  count: { color: 'var(--text-tertiary)', fontSize: '12px', marginLeft: 'auto', whiteSpace: 'nowrap' },
  table: { width: '100%', borderCollapse: 'collapse', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' },
  th: { textAlign: 'left', fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase', color: 'var(--text-tertiary)', padding: '10px 12px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-raised)', whiteSpace: 'nowrap' },
  td: { fontSize: '12.5px', color: 'var(--text-primary)', padding: '9px 12px', borderBottom: '1px solid var(--border-subtle)', verticalAlign: 'top' },
  clave: { fontFamily: 'var(--font-mono, monospace)', fontSize: '12px', fontWeight: 650, color: 'var(--text-secondary)', whiteSpace: 'nowrap' },
  apuRow: { cursor: 'pointer', ':hover': { background: 'var(--bg-raised)' } },
  apuClave: { display: 'inline-flex', alignItems: 'center', gap: '4px', color: 'var(--accent)', cursor: 'pointer' },
  tipoBadge: { display: 'inline-block', fontSize: '11px', fontWeight: 650, padding: '1px 8px', borderRadius: '999px', background: 'var(--bg-overlay)', color: 'var(--text-secondary)', whiteSpace: 'nowrap' },
  tipoApu: { background: 'var(--oficial-weak)', color: 'var(--oficial)' },
  num: { textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
  compWrap: { background: 'var(--bg-raised)', padding: '8px 12px 12px 30px' },
  compTitle: { fontSize: '11px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: '4px 0 8px' },
  compTable: { width: '100%', borderCollapse: 'collapse' },
  compTd: { fontSize: '12px', color: 'var(--text-secondary)', padding: '5px 10px', borderBottom: '1px solid var(--border-subtle)' },
  pager: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px', marginTop: '16px' },
  empty: { color: 'var(--text-tertiary)', fontSize: '13px', padding: '24px 4px', textAlign: 'center' },
});

export function CatalogDetailPage() {
  const styles = useStyles();
  const { id = '' } = useParams();
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const money = useMemo(() => formatoDeMoneda(catalog?.currency).money, [catalog?.currency]);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState<CatalogResource[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [comps, setComps] = useState<Record<string, CatalogComponent[] | 'loading'>>({});
  // Filtro para encontrar los renglones que no se pueden usar. En los catálogos
  // OFICIALES lo ve solo Ecosoft, que es quien los corrige; en el catálogo propio de
  // la empresa lo ve su dueño, que es quien puede arreglar el archivo y subirlo de
  // nuevo. Misma regla que la leyenda del listado y que el API (shared).
  const isEcosoftStaff = useIsEcosoftStaff();
  const verDefectos = !!catalog && puedeVerDefectosDelCatalogo(catalog.kind, isEcosoftStaff);
  const [problema, setProblema] = useState<'sin_unidad' | 'sin_precio' | null>(null);

  useEffect(() => { getCatalog(id).then(setCatalog).catch(() => {}); }, [id]);

  // Debounce de búsqueda.
  useEffect(() => { const t = setTimeout(() => { setDebouncedQ(q); setOffset(0); }, 350); return () => clearTimeout(t); }, [q]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listCatalogResources(id, { q: debouncedQ || undefined, limit: PAGE, offset, problema: problema ?? undefined })
      .then((d) => { if (!cancelled) { setRows(d.resources); setTotal(d.total); setError(null); } })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiClientError ? e.message : 'Error cargando recursos'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, debouncedQ, offset, problema]);

  const verProblema = useCallback((p: 'sin_unidad' | 'sin_precio') => {
    setProblema((cur) => (cur === p ? null : p));
    setOffset(0);
  }, []);

  const toggle = useCallback((clave: string) => {
    setExpanded((cur) => (cur === clave ? null : clave));
    if (!comps[clave]) {
      setComps((c) => ({ ...c, [clave]: 'loading' }));
      listCatalogComponents(id, { parent: clave })
        .then((d) => setComps((c) => ({ ...c, [clave]: d.components })))
        .catch(() => setComps((c) => ({ ...c, [clave]: [] })));
    }
  }, [id, comps]);

  const page = Math.floor(offset / PAGE) + 1;
  const pages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <div className={styles.page}>
      <AppTopBar />
      <Breadcrumbs items={[{ label: 'Inicio', to: '/projects' }, { label: 'Catálogos de referencia', to: '/catalogs' }, { label: catalog?.name ?? 'Catálogo' }]} />
      <div className={styles.container}>
        <Text as="h1" className={styles.title}>{catalog?.name ?? 'Catálogo'}</Text>
        <Text className={styles.subtitle}>
          {catalog ? `${catalog.resourcesCount.toLocaleString()} recursos · ${catalog.compuestosCount.toLocaleString()} APUs (matrices)` : '…'}
        </Text>

        {verDefectos && catalog && ((catalog.resourcesSinUnidad ?? 0) > 0 || (catalog.resourcesSinPrecio ?? 0) > 0) && (
          <div className={styles.aviso}>
            <WarningRegular />
            <span>Recursos que no se pueden usar para poner precios:</span>
            {(catalog.resourcesSinUnidad ?? 0) > 0 && (
              <Button size="small" appearance={problema === 'sin_unidad' ? 'primary' : 'secondary'} onClick={() => verProblema('sin_unidad')}>
                {catalog.resourcesSinUnidad!.toLocaleString()} sin unidad
              </Button>
            )}
            {(catalog.resourcesSinPrecio ?? 0) > 0 && (
              <Button size="small" appearance={problema === 'sin_precio' ? 'primary' : 'secondary'} onClick={() => verProblema('sin_precio')}>
                {catalog.resourcesSinPrecio!.toLocaleString()} sin precio
              </Button>
            )}
          </div>
        )}

        <div className={styles.toolbar}>
          <Input
            className={styles.search}
            contentBefore={<SearchRegular />}
            placeholder="Buscar por descripción o clave…"
            value={q}
            onChange={(_, d) => setQ(d.value)}
          />
          <span className={styles.count}>{total.toLocaleString()} resultados</span>
        </div>

        {error && <Text style={{ color: 'var(--bad)' }}>{error}</Text>}
        {loading ? (
          <Spinner label="Cargando recursos…" />
        ) : rows.length === 0 ? (
          <div className={styles.empty}>Sin recursos para esta búsqueda.</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>Clave</th>
                <th className={styles.th}>Descripción</th>
                <th className={styles.th}>Unidad</th>
                <th className={styles.th}>Tipo</th>
                <th className={styles.th}>Familia</th>
                <th className={styles.th} style={{ textAlign: 'right' }}>Precio</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isApu = r.tipo === 7;
                const open = expanded === r.clave;
                const cs = comps[r.clave];
                return (
                  <Fragment key={r.clave}>
                    <tr
                      className={isApu ? styles.apuRow : undefined}
                      onClick={isApu ? () => toggle(r.clave) : undefined}
                    >
                      <td className={`${styles.td} ${styles.clave}`}>
                        {isApu ? (
                          <span className={styles.apuClave}>
                            {open ? <ChevronDownRegular /> : <ChevronRightRegular />}{r.clave}
                          </span>
                        ) : r.clave}
                      </td>
                      <td className={styles.td}>{r.descripcion}</td>
                      <td className={styles.td}>{r.unidad ?? '—'}</td>
                      <td className={styles.td}><span className={`${styles.tipoBadge}${isApu ? ` ${styles.tipoApu}` : ''}`}>{tipoLabel(r.tipo)}</span></td>
                      <td className={styles.td}>{r.familia ?? '—'}</td>
                      <td className={`${styles.td} ${styles.num}`}>{money(r.precio)}</td>
                    </tr>
                    {isApu && open && (
                      <tr>
                        <td className={styles.compWrap} colSpan={6}>
                          {cs === 'loading' || cs === undefined ? (
                            <Spinner size="tiny" label="Cargando composición…" />
                          ) : cs.length === 0 ? (
                            <Text style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>Este APU no tiene composición cargada.</Text>
                          ) : (
                            <>
                              <div className={styles.compTitle}>Composición ({cs.length} insumos)</div>
                              <table className={styles.compTable}>
                                <tbody>
                                  {cs.map((c, i) => (
                                    <tr key={`${c.childClave}-${i}`}>
                                      <td className={styles.compTd} style={{ width: 90 }}><span className={styles.tipoBadge}>{c.childTipo != null ? tipoLabel(c.childTipo) : '—'}</span></td>
                                      <td className={`${styles.compTd} ${styles.clave}`} style={{ width: 100 }}>{c.childClave}</td>
                                      <td className={styles.compTd}>{c.childDescripcion ?? ''}</td>
                                      <td className={`${styles.compTd} ${styles.num}`} style={{ width: 110 }}>{qty(c.cantidad)} {c.childUnidad ?? ''}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}

        {pages > 1 && (
          <div className={styles.pager}>
            <Button appearance="subtle" size="small" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>← Anterior</Button>
            <Text style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Página {page} de {pages}</Text>
            <Button appearance="subtle" size="small" disabled={page >= pages} onClick={() => setOffset(offset + PAGE)}>Siguiente →</Button>
          </div>
        )}
      </div>
    </div>
  );
}
